import React, { Fragment } from 'react';
import { Controller, useFieldArray } from 'react-hook-form';
import { defaultCondition, defaultLogicOp } from '../types';
import useFilterWidgetFormContext from '../utils/useFilterWidgetFormContext';
import { TextButton } from '../../Button';
import * as FilterWidgetBasicUI from './FilterWidgetBasicUI';
import FilterWidgetLogicOp from './FilterWidgetLogicOp';
import FilterWidgetCondition from './FilterWidgetCondition';
import KubitTooltip from '../../KubitTooltip';

/**
 * This is a main component for a "basic" Filter widget flow.
 * Here we control conditions and logicOps which are both arrays.
 * Amount of logicOps is always equal to: (amount of conditions - 1).
 */

interface IProps {
  maxConditions: number;
  maxPredicates: number;
}

const FilterWidgetBasic = ({ maxConditions, maxPredicates }: IProps) => {
  const { control } = useFilterWidgetFormContext();

  const {
    fields: conditions,
    append: addCondition,
    remove: removeCondition,
  } = useFieldArray({
    name: 'conditions',
    control,
    shouldUnregister: true,
  });

  const {
    fields: logicOps,
    append: addLogicOp,
    remove: removeLogicOp,
  } = useFieldArray({
    name: 'logicOps',
    control,
    shouldUnregister: true,
  });

  const handleAddCondition = () => {
    addCondition(defaultCondition);
    addLogicOp(defaultLogicOp);
  };

  const handleRemoveCondition = (conditionIndex: number) => {
    removeCondition(conditionIndex);

    if (conditions.length === 2) {
      removeLogicOp();
    } else {
      removeLogicOp(conditionIndex - 1);
    }
  };

  const allowAddCondition = conditions.length < maxConditions;

  return (
    <>
      <FilterWidgetBasicUI.Container>
        <FilterWidgetBasicUI.Wrapper>
          {conditions.map((condition, conditionIndex) => (
            <Fragment key={condition.id}>
              <FilterWidgetCondition
                conditionIndex={conditionIndex}
                conditionsLength={conditions.length}
                onRemoveLast={() => handleRemoveCondition(conditionIndex)}
                maxPredicates={maxPredicates}
              />
              {logicOps?.[conditionIndex] && (
                <FilterWidgetBasicUI.LogicOpContainer>
                  <Controller
                    name={`logicOps.${conditionIndex}`}
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <FilterWidgetLogicOp
                        type="condition"
                        operator={value}
                        onChange={onChange}
                      />
                    )}
                  />
                </FilterWidgetBasicUI.LogicOpContainer>
              )}
            </Fragment>
          ))}
        </FilterWidgetBasicUI.Wrapper>
      </FilterWidgetBasicUI.Container>
      {allowAddCondition && (
        <FilterWidgetBasicUI.ActionsContainer>
          <KubitTooltip content={'Add another filter group'} placement="bottom">
            <TextButton onClick={handleAddCondition}>+ Add Group</TextButton>
          </KubitTooltip>
        </FilterWidgetBasicUI.ActionsContainer>
      )}
    </>
  );
};

export default FilterWidgetBasic;
